import React from 'react';
import { Formik, Form } from 'formik';
import { Box } from '@mui/material';
import type { PLRFormProps } from '../../../../../../types/preloan';
import { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import UploadDocumentsList from '../../../../../../components/UploadDocuments/redesign';
import { patchCheckDocuments, postPreApprove, postUploadDocuments } from '../../../../../../queries/preloan/v3';
import { getInterestRate } from '../../../../../../queries/preloan';
import {
  analyticsErrorPageName, analyticsErrorPageProps, DOCUMENT_READ_ERRORS, documentErrorText,
} from './components/errorMessageSwitch';
import { partnerErrorRedirectURL, preLoanRequestStep } from '../../formLogic';
import analytics, { addPreApproveId } from '../../../../../../utils/analytics';
import { inviteePlrCategory } from '../../../ComplementInvite/analyticsPages';
import { fieldsFromPreApprove } from '../02_dataFetching/components/helpers';
import PlrShell from '../../../landing/PlrShell';
import PlrWaiting, { IndeterminateBar } from '../../../landing/PlrWaiting';
import AgentLink from '../../../landing/AgentLink';
import {
  PlrButton, PlrConsent, PlrLead, PlrNotice, PlrTitle, ProjectLabel,
} from '../../../landing/ui';
import { partnerFromSchema, useShowHero } from '../../../landing/partner';
import { projectEntityLabel } from '../../../heroVariant';
import { plr } from '../../../landing/tokens';
import { DOCUMENTS_CONSENT_TEXT, privacyPolicyUrl } from '../../../legal';

const MAX_TRIES = 3;
const GENERIC_ERROR = 'Ocurrió un error. Inténtalo en otro momento o contáctanos.';
const ANALYZING_MESSAGES = [
  'Recibimos tus documentos…',
  'Leyendo tus documentos…',
  'Revisando tus ingresos…',
];

const documentsTitle = (schema: FormSchemaV3) => {
  const settlements = schema.settlementsRequired || 1;
  if (schema.needsSettlement && schema.needsPensionStatement) return 'Sube tus documentos';
  if (schema.needsSettlement) {
    return settlements > 1 ? `Sube tus últimas ${settlements} liquidaciones` : 'Sube tu última liquidación';
  }
  return 'Sube tus cotizaciones';
};

// partnerRedirect: como el flujo anterior, los errores de lectura/revisión redirigen a los
// partners con URL de error; un fallo al subir a S3 solo muestra el mensaje.
type DocumentsError = { message: string, attempt?: number, partnerRedirect?: boolean };

export const gtmDocumentsSubpasoSwitch = (
  needsSettlement: boolean,
  needsPensionStatement: boolean,
) => {
  if (needsPensionStatement && needsSettlement) {
    return 'Carga de última liquidación y últimas 24 cotizaciones';
  }
  if (needsPensionStatement) {
    return 'Carga de última liquidación';
  }
  return 'Carga de últimas 24 cotizaciones';
};

export default function UploadDocumentsForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const {
    formRef,
    formSchema,
    handleEnableLoading,
    handleStep,
    handleCurrentData,
  } = props;
  const partner = partnerFromSchema(formSchema);
  // Consentimiento expreso para los datos sensibles de los documentos (Ley 21.719, art. 16).
  const [docsConsent, setDocsConsent] = React.useState(false);
  const toggleDocsConsent = () => {
    analytics.track('Click Checkbox', { text: 'Autorizo el uso de mis documentos', initiator: 'usuario' });
    setDocsConsent((value) => !value);
  };
  const showHero = useShowHero(formSchema);
  const [ready, setReady] = React.useState(false);
  const [upload, setUpload] = React.useState(false);
  const [phase, setPhase] = React.useState<'select' | 'uploading' | 'analyzing' | 'redirecting'>('select');
  const [error, setError] = React.useState<DocumentsError | null>(null);
  const [numTries, setNumTries] = React.useState(1);
  // Cambia para volver a montar las tarjetas vacías después de un error de lectura.
  const [listKey, setListKey] = React.useState(0);
  const failWith = (message: string, partnerRedirect = true) => {
    setPhase('select');
    setError({ message: message || GENERIC_ERROR, partnerRedirect });
  };
  const redirectToPartnerOnError = () => {
    const redirectURL = partnerErrorRedirectURL(
      formSchema.origin,
      formSchema.rut,
      formSchema.config,
    );
    if (redirectURL) {
      setTimeout(() => { window.location.href = redirectURL; }, 1000);
    }
  };

  const handleUploadOk = async () => {
    setError(null);
    setPhase('analyzing');
    handleEnableLoading(true);
    try {
      const { data, status } = await patchCheckDocuments(formSchema.PLRtoken, numTries);
      switch (data.plr.step) {
        case 'DATA_OK': {
          const { data: preApproveData } = await postPreApprove(formSchema.PLRtoken);
          if (preApproveData.preApproves?.length > 0) {
            const mainPreApprove = preApproveData.preApproves.find((pa) => pa.productSubtype === 'main');
            const selectedPreApprove = mainPreApprove || preApproveData.preApproves[0];
            addPreApproveId(selectedPreApprove.id);
          }
          analytics.page(
            inviteePlrCategory(formSchema.complementToken, 'PLR - DOCUMENTOS'),
            'LECTURA DOCUMENTOS EXITOSA',
          );
          switch (preApproveData.plr.step) {
            case 'LOAN_SIMULATION': {
              const interestRate = await getInterestRate(formSchema.PLRtoken);
              handleCurrentData({
                ...formSchema,
                // El paso es el de pre-approve (check-documents responde DATA_OK).
                step: preApproveData.plr.step,
                loanParams: interestRate.data,
              });
              return handleStep(preLoanRequestStep.LOAN_SIMULATION);
            }
            case 'READY_FOR_LOAN': {
              handleCurrentData({
                ...formSchema,
                step: preApproveData.plr.step,
                preApproves: preApproveData.preApproves,
                ...fieldsFromPreApprove(preApproveData),
              });
              return handleStep(preLoanRequestStep.READY_FOR_LOAN);
            }
            case 'ARCHIVED_FAIL':
              handleCurrentData({
                ...formSchema,
                step: 'ARCHIVED_FAIL',
                firstName: data.plr.firstName,
                lastName: data.plr.lastName,
                rut: data.plr.idNumber,
                results: preApproveData.results,
                ...fieldsFromPreApprove(preApproveData),
                origin: preApproveData.plr.origin,
              });
              return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
            default:
              return failWith(GENERIC_ERROR);
          }
        }
        case 'FILL_FORM': {
          const rawDataForm = data.form;
          const dataForm = {} as FormSchemaV3['dataForm'];
          const contactForm = {} as FormSchemaV3['contactForm'];
          Object.keys(rawDataForm).forEach((key) => {
            let value: string;
            if (key === 'phone') {
              value = data.plr.phone ? `+56 ${data.plr.phone}` : '+56 ';
              contactForm[key] = { value, type: rawDataForm[key], ok: true };
            } else if (key === 'email') {
              value = data.plr.email || '';
              contactForm[key] = { value, type: rawDataForm[key], ok: true };
            } else {
              value = formSchema.dataForm[key].value;
              dataForm[key] = { value, type: rawDataForm[key] };
            }
          });
          handleCurrentData({
            ...formSchema,
            firstName: data.plr.firstName,
            lastName: data.plr.lastName,
            step: data.plr.step,
            dataForm,
            contactForm,
          });
          if (status === 206) {
            analytics.page(
              inviteePlrCategory(formSchema.complementToken, 'PLR - DOCUMENTOS'),
              'FIN REINTENTOS LECTURA DE DOCUMENTOS',
            );
            setTimeout(() => handleStep(preLoanRequestStep.FILL_FORM), 10000);
            // Como antes: al llegar al máximo de intentos se redirige a los partners con URL de
            // error (el flujo anterior lo hacía marcando el error).
            redirectToPartnerOnError();
            return setPhase('redirecting');
          }
          analytics.page(
            inviteePlrCategory(formSchema.complementToken, 'PLR - DOCUMENTOS'),
            'LECTURA DOCUMENTOS EXITOSA',
          );
          return handleStep(preLoanRequestStep.FILL_FORM);
        }
        case 'READY_FOR_LOAN': {
          analytics.page(
            inviteePlrCategory(formSchema.complementToken, 'PLR - DOCUMENTOS'),
            'LECTURA DOCUMENTOS EXITOSA',
          );
          handleCurrentData({
            ...formSchema,
            firstName: data.plr.firstName,
            lastName: data.plr.lastName,
            step: data.plr.step,
          });
          return handleStep(preLoanRequestStep.READY_FOR_LOAN);
        }
        default:
          return failWith(GENERIC_ERROR);
      }
    } catch (e) {
      const readError = DOCUMENT_READ_ERRORS.includes(e as string);
      if (readError) {
        setNumTries((prevState) => prevState + 1);
      }
      analytics.page(
        'PLR - ERROR LECTURA DOCS',
        analyticsErrorPageName[e as string] || 'FALLO DESCONOCIDO',
        analyticsErrorPageProps(numTries)[e as string] || { attempts: numTries },
      );
      // Tarjetas vacías otra vez: hay que elegir archivos nuevos.
      setListKey((key) => key + 1);
      setReady(false);
      setPhase('select');
      return setError({
        message: documentErrorText(e as string | number),
        attempt: readError ? numTries : undefined,
        partnerRedirect: true,
      });
    }
  };

  const handleSend = () => {
    analytics.track('Click Boton', { initiator: 'usuario', text: 'Enviar documentos' });
    setError(null);
    setPhase('uploading');
    setUpload(true);
  };
  const handleUploaded = () => {
    setUpload(false);
    // Mismo respiro que el diálogo anterior antes de pedir la lectura.
    setPhase('analyzing');
    setTimeout(() => handleUploadOk(), 1000);
  };
  const handleUploadError = () => {
    setUpload(false);
    failWith('No pudimos subir tus archivos. Vuelve a intentarlo y, si sigue fallando, contáctanos.', false);
  };
  const handleFirstChoose = () => {
    const category = inviteePlrCategory(formSchema.complementToken, 'PLR - DOCUMENTOS');
    if (formSchema.needsPensionStatement && formSchema.needsSettlement) {
      analytics.page(category, 'SUBIR ULTIMA LIQUIDACIÓN Y COTIZACIONES');
    } else if (formSchema.needsSettlement) {
      analytics.page(category, 'SUBIR ULTIMA LIQUIDACIÓN');
    } else if (formSchema.needsPensionStatement) {
      analytics.page(category, 'SUBIR COTIZACIONES');
    }
  };

  React.useEffect(() => {
    if (error?.partnerRedirect) redirectToPartnerOnError();
  }, [error]);

  React.useEffect(() => {
    const category = inviteePlrCategory(
      formSchema.complementToken,
      formSchema.isRedirect ? `PLR - ${formSchema.isRedirect}` : 'PLR - DOCUMENTOS',
    );
    analytics.page(category, 'ADJUNTAR DOCUMENTOS');
    handleCurrentData({ ...formSchema, isRedirect: false });
    handleEnableLoading(true);
  }, []);

  return (
    <Formik
      innerRef={formRef}
      initialValues={formSchema}
      onSubmit={() => { }}
    >
      {({ values }) => (
        <Form style={{ width: '100%' }}>
          <PlrShell
            partner={partner}
            showHero={showHero}
            progress={{ step: 2, label: 'Tus ingresos' }}
            testId="preloan-upload-documents-step"
          >
            {showHero && (
              <ProjectLabel hideOnPanel>
                {projectEntityLabel(formSchema.partnerEntityName, formSchema.partner)}
              </ProjectLabel>
            )}
            <PlrTitle size="step" sx={{ mt: showHero ? '-6px' : 0 }}>
              {documentsTitle(values)}
            </PlrTitle>
            <PlrLead>
              Con ellos confirmamos tus ingresos y terminamos de calcular tu resultado.
            </PlrLead>
            <Box sx={{
              display: 'flex', flexDirection: 'column', flex: 1, gap: '16px',
            }}
            >
              <UploadDocumentsList
                key={listKey}
                needsSettlement={values.needsSettlement}
                needsPensionStatement={values.needsPensionStatement}
                settlementsRequired={values.settlementsRequired || 1}
                pensionStatementsRequired={values.pensionStatementsRequired || 12}
                getDocumentsUrl={(payslipExt, pensionExt) => postUploadDocuments(
                  values.PLRtoken,
                  payslipExt,
                  pensionExt,
                )}
                onReadyChange={setReady}
                upload={upload}
                onUploaded={handleUploaded}
                onUploadError={handleUploadError}
                onFirstChoose={handleFirstChoose}
              />
              {error && (
                <PlrNotice tone="error" testId="preloan-documents-error">
                  {error.message}
                  {error.attempt && (
                    <Box
                      component="span"
                      sx={{
                        display: 'block', mt: '6px', fontWeight: 700, color: plr.ink,
                      }}
                    >
                      {`Intento ${error.attempt} de ${MAX_TRIES}`}
                    </Box>
                  )}
                </PlrNotice>
              )}
              {phase === 'analyzing' && (
                <PlrWaiting
                  testId="preloan-documents-analyzing"
                  messages={ANALYZING_MESSAGES}
                  detail="No cierres esta página."
                />
              )}
              {phase === 'redirecting' && (
                <PlrNotice tone="info" testId="preloan-documents-max-tries">
                  Llegaste al máximo de intentos para subir documentos. Te llevamos al siguiente
                  paso para que completes tus datos.
                  <Box sx={{ mt: '10px' }}><IndeterminateBar label="Cambiando de paso" /></Box>
                </PlrNotice>
              )}
              {(phase === 'select' || phase === 'uploading') && (
                <Box sx={{
                  mt: 'auto', display: 'flex', flexDirection: 'column', gap: '14px',
                }}
                >
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: '18px',
                      fontSize: { xs: 13, sm: 13.5 },
                      lineHeight: 1.5,
                      color: plr.muted,
                      '& li + li': { mt: '2px' },
                    }}
                  >
                    <li>PDF, JPG o PNG de hasta 5 MB.</li>
                    <li>
                      Que se lea bien: el PDF original, un pantallazo o una foto nítida del
                      documento completo, no una foto de la pantalla.
                    </li>
                  </Box>
                  <PlrConsent
                    testId="preloan-documents-consent"
                    checked={docsConsent}
                    onToggle={toggleDocsConsent}
                    disabled={phase === 'uploading'}
                  >
                    {`${DOCUMENTS_CONSENT_TEXT} `}
                    <a
                      href={privacyPolicyUrl(formSchema.partner)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => analytics.track('Click Link', { text: 'política de privacidad', initiator: 'usuario' })}
                    >
                      Política de privacidad
                    </a>
                    .
                  </PlrConsent>
                  <PlrButton
                    testId="preloan-upload-documents-button"
                    onClick={handleSend}
                    disabled={!ready || !docsConsent}
                    loading={phase === 'uploading'}
                  >
                    Enviar documentos
                  </PlrButton>
                  {values.partner === 'Banca.Me' && (
                    <Box sx={{ fontSize: 13.5, color: plr.body, textAlign: 'center' }}>
                      ¿Problemas con tus documentos?
                      {' '}
                      <AgentLink cta="documentos" />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </PlrShell>
        </Form>
      )}

    </Formik>
  );
}
